import { useCallback, useEffect, useState } from 'react';
import './ProductPopup.css';
import ProductItem from '../ProductItem/ProductItem';
import { FaChevronLeft, FaChevronRight, FaSearch, FaTimes } from 'react-icons/fa';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

const ProductPopup = ({ setIsModalOpen, updateProduct }) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, flushSearch] = useDebouncedValue(search, 400);
    const [productsIndex, setProductsIndex] = useState(0);
    const [products, setProducts] = useState([]);
    const [pageSize] = useState(10);

    const getProducts = (index) => {
        fetch(`${process.env.REACT_APP_API}/api/getProducts/${index}/${pageSize}`, {})
            .then((response) => {
                if (response.status === 200) {
                    response.json().then((data) => {
                        setProducts(data);
                    });
                }
            })
            .catch(() => setProducts([]));
    };

    const getProductsByName = (name, index) => {
        fetch(
            `${process.env.REACT_APP_API}/api/getProductsByName/${name.trim()}/${index}/${pageSize}`,
            {}
        )
            .then((response) => {
                if (response.status === 200) {
                    response.json().then((data) => {
                        setProducts(data);
                    });
                }
            })
            .catch(() => setProducts([]));
    };

    const closeModal = useCallback(() => {
        document.getElementById('body').classList.remove('modal-open');
        setIsModalOpen(false);
    }, [setIsModalOpen]);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.getElementById('body').classList.add('modal-open');
        return () => {
            document.getElementById('body').classList.remove('modal-open');
        };
    }, []);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') closeModal();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [closeModal]);

    useEffect(() => {
        const q = debouncedSearch.trim();
        setProductsIndex(0);
        if (q !== '') {
            getProductsByName(q, 0);
        } else {
            getProducts(0);
        }
    }, [debouncedSearch, pageSize]);

    const handleChangeSearch = (e) => {
        setSearch(e.target.value);
    };

    const handleSearch = useCallback(() => {
        flushSearch();
    }, [flushSearch]);

    const handleUpdateProduct = useCallback(
        (data, amount) => {
            updateProduct(data, amount);
            setSearch('');
            setProductsIndex(0);
            getProducts(0);
        },
        [updateProduct]
    );

    const prevPage = () => {
        if (productsIndex === 0) return;
        const nextIndex = productsIndex - 1;
        if (debouncedSearch.trim() !== '') {
            getProductsByName(debouncedSearch.trim(), nextIndex);
        } else {
            getProducts(nextIndex);
        }
        setProductsIndex(nextIndex);
    };

    const nextPage = () => {
        const nextIndex = productsIndex + 1;
        if (debouncedSearch.trim() !== '') {
            getProductsByName(debouncedSearch.trim(), nextIndex);
        } else {
            getProducts(nextIndex);
        }
        setProductsIndex(nextIndex);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const pageLabel = productsIndex + 1;

    return (
        <div
            className="ppv2-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ppv2-title"
            onClick={closeModal}
        >
            <div className="ppv2-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="ppv2-header">
                    <h2 id="ppv2-title" className="ppv2-title">
                        Add products
                    </h2>
                    <button
                        type="button"
                        className="ppv2-icon-btn"
                        onClick={closeModal}
                        aria-label="Close"
                    >
                        <FaTimes aria-hidden />
                    </button>
                </div>

                <div className="ppv2-toolbar">
                    <div className="ppv2-search">
                        <input
                            className="ppv2-input"
                            value={search}
                            onChange={handleChangeSearch}
                            onKeyDown={handleKeyDown}
                            placeholder="Search name or SKU…"
                            aria-label="Search products"
                        />
                        <button type="button" className="ppv2-btn ppv2-btn--primary" onClick={handleSearch}>
                            <FaSearch aria-hidden />
                            Search
                        </button>
                    </div>
                    <div className="ppv2-pagination" aria-label="Product pages">
                        <button
                            type="button"
                            className="ppv2-btn ppv2-btn--ghost"
                            onClick={prevPage}
                            disabled={productsIndex === 0}
                        >
                            <FaChevronLeft aria-hidden />
                            Prev
                        </button>
                        <span className="ppv2-page-indicator">Page {pageLabel}</span>
                        <button type="button" className="ppv2-btn ppv2-btn--ghost" onClick={nextPage}>
                            Next
                            <FaChevronRight aria-hidden />
                        </button>
                    </div>
                </div>

                <div className="ppv2-body">
                    {products.length > 0 ? (
                        <div className="ppv2-grid">
                            {products.map((product) => {
                                if (!product) return null;
                                return (
                                    <ProductItem
                                        key={product.id}
                                        data={product}
                                        small
                                        updateProduct={handleUpdateProduct}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <p className="ppv2-empty">No products to show. Try another search or page.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductPopup;
